/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Dmitry Pavlov
 * @version 1.22.3
 * @since 1.14.0
 */

import * as Yup from 'yup';

import EFormValidation from '@modules/form/const/formValidation';
import { ICorporateFormTranslation } from '@modules/internationalization/types/corporate';

export enum AccountInfoDeliveryFields {
    address = 'address',
    email = 'email',
    note = 'note',
    contactPerson = 'contactPerson',
    phone = 'phone',
    useEdm = 'useEdm',
    useEmail = 'useEmail',
    usePost = 'usePost',
    useCourier = 'useCourier',
}

export const SubscriberIdPersonalDataEditValidationSchema = (
    translation: ICorporateFormTranslation['validation'],
): Yup.AnySchema => {
    Yup.setLocale({
        array: {
            min: translation[EFormValidation.fieldRequired],
        },
        mixed: {
            notType: translation[EFormValidation.fieldRequired],
            required: translation[EFormValidation.fieldRequired],
        },
        string: {
            email: translation[EFormValidation.fieldEmail],
            matches: translation[EFormValidation.fieldNotType],
        },
    });

    const isRequired = (useCheck: AccountInfoDeliveryFields, schema: Yup.AnySchema) =>
        schema.when(useCheck, {
            is: (useCheck: boolean) => useCheck,
            then: schema.required(),
            otherwise: schema.nullable(true),
        });

    const usePostOrCourier = AccountInfoDeliveryFields.usePost || AccountInfoDeliveryFields.useCourier;

    return Yup.object().shape({
        [AccountInfoDeliveryFields.useEdm]: Yup.boolean(),
        [AccountInfoDeliveryFields.useEmail]: Yup.boolean(),
        [AccountInfoDeliveryFields.usePost]: Yup.boolean(),
        [AccountInfoDeliveryFields.useCourier]: Yup.boolean(),
        [AccountInfoDeliveryFields.address]: Yup.object().when(
            usePostOrCourier, {
                is: (useCheck: boolean) => useCheck,
                then: Yup.object().shape({
                    addressString: Yup.string().required(),
                }),
                otherwise: Yup.object(),
            },
        ),
        [AccountInfoDeliveryFields.contactPerson]: isRequired(usePostOrCourier, Yup.string()),
        [AccountInfoDeliveryFields.phone]: isRequired(usePostOrCourier
            , Yup.string().min(10)),
        [AccountInfoDeliveryFields.note]: Yup.string().nullable(true),
        // Поля для доставки по эл. почте
        [AccountInfoDeliveryFields.email]: isRequired(AccountInfoDeliveryFields.useEmail, Yup.string().email()),
    });
}
